import { isDefined } from '@interacta-shared/util';
import { TranslateService } from '@ngx-translate/core';
import {
    BulkWorkflowTransitionOperation,
    OperationState,
} from '../models/bulk-workflow-transition.model';

const labelRoot = 'BULK_WORKFLOW_TRANSITION.BANNER.';
export const buildBulkWorkflowTransitionBannerTitle = (
    operation: BulkWorkflowTransitionOperation,
    translateService: TranslateService,
): string => {
    const error = buildUpdatedWithErrorLabel(operation, translateService);
    if (operation.state === OperationState.IN_PROGRESS) {
        const progressLabel = translateService.instant(
            labelRoot + 'PROCESSED_PROGRESS',
            {
                processed: countProcessed(operation),
                total: operation.posts.length,
            },
        );

        const errorLabel = error ? `(${error})` : null;

        return [progressLabel, errorLabel].filter(isDefined).join(' ');
    } else {
        const finished = String(
            translateService.instant(labelRoot + 'FINISHED'),
        );
        const completed = buildUpdatedOkLabel(operation, translateService);
        const aborted = buildAbortedLabel(operation, translateService);

        const counters = [completed, error, aborted]
            .filter(isDefined)
            .join(', ');
        return `${finished} ${counters}`;
    }
};

export const buildUpdatedOkLabel = (
    operation: BulkWorkflowTransitionOperation,
    translateService: TranslateService,
): string | null => {
    const counter = operation.successPostsCount;

    return getOkLabel(counter, translateService);
};

export const buildUpdatedWithErrorLabel = (
    operation: BulkWorkflowTransitionOperation,
    translateService: TranslateService,
): string | null => {
    const counter = operation.failedPostsCount;

    return getErrorLabel(counter, translateService);
};

export const buildAbortedLabel = (
    operation: BulkWorkflowTransitionOperation,
    translateService: TranslateService,
): string | null => {
    const counter = operation.posts.length - countProcessed(operation);

    return getAbortedLabel(counter, translateService);
};

export const getOkLabel = (
    counter: number,
    translateService: TranslateService,
): string | null => {
    if (counter > 0) {
        return translateService.instant(
            labelRoot +
                (counter === 1 ? 'COMPLETED_OK_SING' : 'COMPLETED_OK_PLUR'),
            { counter },
        );
    }
    return null;
};

export const getErrorLabel = (
    counter: number,
    translateService: TranslateService,
): string | null => {
    if (counter > 0) {
        return translateService.instant(
            labelRoot +
                (counter === 1 ? 'COMPLETED_KO_SING' : 'COMPLETED_KO_PLUR'),
            { counter },
        );
    }
    return null;
};

export const getAbortedLabel = (
    counter: number,
    translateService: TranslateService,
): string | null => {
    if (counter > 0) {
        return translateService.instant(
            labelRoot + (counter === 1 ? 'ABORTED_SING' : 'ABORTED_PLUR'),
            { counter },
        );
    }
    return null;
};

const countProcessed = (operation: BulkWorkflowTransitionOperation): number => {
    return operation.successPostsCount + operation.failedPostsCount;
};
